import Uppy, { type UppyFile, type UppyOptions } from "@uppy/core";
import AwsS3, { type AwsS3Part } from "@uppy/aws-s3";
import { CHUNK_SIZE, COMPLETE_TIMEOUT_MS, CONCURRENCY, MULTIPART_THRESHOLD, RETRY_DELAYS } from "./constants";
import { FileFingerprint, ResumeRecord, UploadResource, resumeStore } from "./resumeStore";
import { isSessionGone, postUpload } from "./uploadClient";

/**
 * The one S3 upload engine: an Uppy instance with `@uppy/aws-s3` wired to the
 * backend's shared multipart routes (initialize, sign-parts, list-parts,
 * complete, abort). Media, spatial datasets and field imports differ only in
 * their adapter: where the row comes from and which routes to call.
 *
 * What Uppy already does, and this relies on: it asks for a part's URL just
 * before each PUT and again on every retry, retries failed parts on
 * RETRY_DELAYS, pauses while the browser is offline, and on a resume lists the
 * parts S3 holds and uploads only the rest.
 *
 * What this adds:
 * - A resume record per file (`resumeStore`), so an upload interrupted by a
 *   refresh or a closed tab continues when the same file is selected again.
 * - Failures keep their parts. Uppy aborts a multipart upload whenever a part or
 *   `complete` fails; the abort is skipped then, so Retry resumes from S3. Only
 *   `discard` (the user cancelling) aborts on the backend and removes the row.
 * - Files of 5 MB or less: media signs one PUT for them, which also creates the
 *   row. Every other resource sends all its files as multipart sessions, a small
 *   file as a single part, so each file is completed the moment its own bytes
 *   are in rather than after the rest of its batch.
 */

export type UploadMeta = Record<string, any>;
export type UploadBody = Record<string, any>;
export type EngineFile = UppyFile<UploadMeta, UploadBody>;

export interface UploadAdapter {
  resource: UploadResource;
  /** Base path of the shared actions, e.g. `v2/media/multipart`. */
  multipartPath: string;
  /** The initialize route, and any body fields it needs beyond the file's. */
  initialize: (assetId?: number) => { path: string; body?: Record<string, unknown> };
  /** Field the abort takes so the backend can clean up after the session expired. */
  assetIdField?: "media_id" | "dataset_id";
  contentType: (file: EngineFile) => string;
  /** Name the S3 key is built from. */
  fileName: (file: EngineFile) => string;
  /** Creates the row a multipart upload feeds (media). Otherwise the row comes in `meta.assetId`, or there is none. */
  createAsset?: (file: EngineFile) => Promise<number>;
  /** Deletes a cancelled upload's row when the abort did not. */
  deleteAsset?: (assetId: number) => Promise<void>;
  /** One signed PUT for a file of 5 MB or less, creating its row. `previousAssetId` is the row of an earlier failed attempt. */
  signSinglePut?: (file: EngineFile, previousAssetId?: number) => Promise<{ url: string; key: string; assetId: number }>;
}

export interface UploadEngine {
  uppy: Uppy<UploadMeta, UploadBody>;
  /** The resumable upload on record for this file, or null when there is none or its session has gone. */
  findResumable: (data: File) => Promise<ResumeRecord | null>;
  /** Adds a file. With `resume`, the upload continues from the parts S3 already holds. Returns the Uppy file id. */
  addFile: (data: File, meta?: UploadMeta, resume?: ResumeRecord | null) => string;
  /** Cancels files for good: stops them, discards their parts and removes their rows. Returns the number of rows removed. */
  discard: (fileIds: string[]) => Promise<number>;
  /** Uploads a reload or a closed tab interrupted; selecting the same file again continues one. */
  listInterrupted: () => ResumeRecord[];
  /** Gives up on an interrupted upload: discards its parts and removes its row. */
  discardInterrupted: (record: ResumeRecord) => Promise<void>;
}

interface InitializeResponse {
  upload_id: string;
  file_key: string;
  calculation?: { chunk_size?: number };
}

interface AbortResponse {
  media_deleted?: boolean;
  dataset_deleted?: boolean;
}

const cancelledError = () => new Error("Upload cancelled");

/** A user-facing reason for a failed file. Uppy's own messages for a failed PUT to S3 say nothing useful. */
export const describeUploadError = (error: unknown): string => {
  const message = (error as Error)?.message;
  const status = (error as { source?: { status?: number } })?.source?.status;
  if (message === "Unknown error") return "The connection to file storage was lost. Retry the upload.";
  if (message === "Non 2xx") return `File storage rejected the upload${status ? ` (HTTP ${status})` : ""}. Retry the upload.`;
  if (message === "Request has expired") return "The upload link expired. Retry the upload.";
  return message || "Upload failed";
};

const fingerprintOf = (file: EngineFile): FileFingerprint => {
  const data = file.data as File;
  return { name: data.name ?? file.name ?? "", size: data.size, lastModified: data.lastModified };
};

export const createS3Uppy = (
  adapter: UploadAdapter,
  restrictions: Partial<NonNullable<UppyOptions<UploadMeta, UploadBody>["restrictions"]>> = {}
): UploadEngine => {
  const { resource, multipartPath } = adapter;
  const uppy = new Uppy<UploadMeta, UploadBody>({
    autoProceed: false,
    allowMultipleUploadBatches: true,
    // An empty file has no part to sign (the backend rejects `parts: 0`), so no resource may send one.
    restrictions: { minFileSize: 1, ...restrictions }
  });

  /** Files the user cancelled; requests still in flight for them clean up after themselves. */
  const discarded = new Set<string>();

  const assetIdOf = (file: EngineFile): number | undefined => uppy.getFile(file.id)?.meta.assetId ?? file.meta.assetId;

  // `s3Multipart` is the Uppy file state that makes the next attempt a resume.
  const setResumeState = (fileId: string, state: { uploadId: string; key: string } | undefined) => {
    if (uppy.getFile(fileId)) uppy.setFileState(fileId, { s3Multipart: state } as Partial<EngineFile>);
  };

  /** Points the file at its recorded session, so Retry lists the parts S3 holds and uploads the rest. */
  const keepResumable = (file: EngineFile) => {
    const record = resumeStore.get(resource, fingerprintOf(file));
    if (record) setResumeState(file.id, { uploadId: record.uploadId, key: record.key });
  };

  /** The session is gone: the next attempt starts a new one. */
  const forget = (file: EngineFile) => {
    resumeStore.clear(resource, fingerprintOf(file));
    setResumeState(file.id, undefined);
  };

  const abortSession = (uploadId: string, assetId?: number) =>
    postUpload<AbortResponse>(`${multipartPath}/abort/`, {
      upload_id: uploadId,
      ...(adapter.assetIdField && assetId != null ? { [adapter.assetIdField]: assetId } : {})
    });

  const deleteAssetQuietly = async (assetId: number) => {
    try {
      await adapter.deleteAsset?.(assetId);
    } catch (error) {
      console.error(`[upload] Could not delete ${resource} ${assetId} after a cancel`, error);
    }
  };

  /** Ends a cancelled file's session and removes its row. Resolves true when a row was removed. */
  const cleanUp = async (uploadId: string | undefined, assetId: number | undefined): Promise<boolean> => {
    if (uploadId) {
      try {
        const res = await abortSession(uploadId, assetId);
        if (res?.media_deleted || res?.dataset_deleted) return true;
      } catch (error) {
        // 409: the file already landed for this row, so nothing is discarded. Any other
        // failure (a lost connection, a 500, a session the row would not vouch for) means we
        // cannot know whether the bytes landed, so the row is left to the backend's
        // stale-upload sweep rather than deleted blind.
        if ((error as { status?: number })?.status !== 409) {
          console.error(`[upload] Could not abort ${resource} upload ${uploadId}`, error);
        }
        return false;
      }
    }
    // The session was ended, or never began: the row has nothing behind it.
    if (assetId == null || !adapter.deleteAsset) return false;
    await deleteAssetQuietly(assetId);
    return true;
  };

  const ensureAsset = async (file: EngineFile): Promise<number | undefined> => {
    const known = assetIdOf(file);
    if (known != null || !adapter.createAsset) return known;
    const assetId = await adapter.createAsset(file);
    uppy.setFileMeta(file.id, { assetId });
    if (discarded.has(file.id)) {
      await deleteAssetQuietly(assetId);
      throw cancelledError();
    }
    return assetId;
  };

  const createSession = async (file: EngineFile): Promise<{ uploadId: string; key: string }> => {
    // A session on record means a Retry lost Uppy's own state; continue it rather than start another.
    const existing = resumeStore.get(resource, fingerprintOf(file));
    if (existing) return { uploadId: existing.uploadId, key: existing.key };

    const assetId = await ensureAsset(file);
    const size = file.size ?? 0;
    const { path, body } = adapter.initialize(assetId);
    const init = await postUpload<InitializeResponse>(path, {
      filename: adapter.fileName(file),
      fileType: adapter.contentType(file),
      fileSize: size,
      parts: Math.ceil(size / CHUNK_SIZE),
      ...body
    });
    if (discarded.has(file.id)) {
      await cleanUp(init.upload_id, assetId);
      throw cancelledError();
    }
    if (init.calculation?.chunk_size != null && init.calculation.chunk_size !== CHUNK_SIZE) {
      throw new Error(`The server expects ${init.calculation.chunk_size}-byte parts, not ${CHUNK_SIZE}. Reload the page and try again.`);
    }
    resumeStore.set(resource, fingerprintOf(file), { uploadId: init.upload_id, key: init.file_key, assetId });
    return { uploadId: init.upload_id, key: init.file_key };
  };

  const signPart = async (file: EngineFile, uploadId: string, partNumber: number, signal?: AbortSignal) => {
    try {
      const res = await postUpload<{ urls: Record<string, string> }>(
        `${multipartPath}/sign-parts/`,
        { upload_id: uploadId, part_numbers: [partNumber] },
        { signal }
      );
      resumeStore.touch(resource, fingerprintOf(file));
      return { url: res.urls[String(partNumber)] };
    } catch (error) {
      if (isSessionGone(error)) forget(file);
      throw error;
    }
  };

  const complete = async (file: EngineFile, uploadId: string, parts: AwsS3Part[], signal?: AbortSignal) => {
    try {
      const res = await postUpload<UploadBody>(
        `${multipartPath}/complete/`,
        { upload_id: uploadId, parts: parts.map(({ PartNumber, ETag }) => ({ PartNumber, ETag })) },
        { timeout: COMPLETE_TIMEOUT_MS, signal }
      );
      resumeStore.clear(resource, fingerprintOf(file));
      uppy.setFileMeta(file.id, { fileKey: res.file_key, uploadMethod: "s3_multipart" });
      return res;
    } catch (error) {
      if (isSessionGone(error)) forget(file);
      throw error;
    }
  };

  uppy.use(AwsS3, {
    // Media of 5 MB or less goes as one signed PUT. Every other file is a session,
    // even a one-part one, because a session is completed the moment its own
    // parts are in, whatever else in the batch is still uploading.
    shouldUseMultipart: (file) => !adapter.signSinglePut || (file.size ?? 0) > MULTIPART_THRESHOLD,
    getChunkSize: () => CHUNK_SIZE,
    limit: CONCURRENCY,
    retryDelays: RETRY_DELAYS,

    // Only media's files of 5 MB or less come here; see shouldUseMultipart.
    getUploadParameters: async (file) => {
      if (!adapter.signSinglePut) throw new Error(`A ${resource} file must go up as a multipart session.`);
      const { url, key, assetId } = await adapter.signSinglePut(file, assetIdOf(file));
      uppy.setFileMeta(file.id, { assetId, fileKey: key, uploadMethod: "signed_url" });
      if (discarded.has(file.id)) {
        await deleteAssetQuietly(assetId);
        throw cancelledError();
      }
      return { method: "PUT", url, headers: { "Content-Type": adapter.contentType(file) } };
    },

    createMultipartUpload: (file) => createSession(file),
    signPart: (file, { uploadId, partNumber, signal }) => signPart(file, uploadId, partNumber, signal),

    listParts: async (file, { uploadId, signal }) => {
      try {
        const res = await postUpload<{ parts: AwsS3Part[] }>(`${multipartPath}/list-parts/`, { upload_id: uploadId }, { signal });
        return res.parts;
      } catch (error) {
        if (isSessionGone(error)) forget(file);
        throw error;
      }
    },

    completeMultipartUpload: async (file, { uploadId, parts, signal }) => {
      const res = await complete(file, uploadId, parts, signal);
      return { ...res, location: res.location };
    },

    // Uppy calls this after any failed part or `complete`, not only on a cancel.
    // Keeping the parts is what lets Retry resume; `discard` aborts a cancel.
    abortMultipartUpload: async (file) => {
      if (!discarded.has(file.id)) keepResumable(file);
    }
  });

  uppy.on("upload-error", (file, error) => {
    if (!file || !uppy.getFile(file.id)) return;
    uppy.setFileState(file.id, { error: describeUploadError(error) });
    // Uppy clears a failed file's session before reporting the failure; restore it so Retry resumes.
    if (!discarded.has(file.id)) keepResumable(file);
  });

  return {
    uppy,

    findResumable: async (data) => {
      const record = resumeStore.get(resource, data);
      if (!record) return null;
      try {
        await postUpload(`${multipartPath}/list-parts/`, { upload_id: record.uploadId });
        return record;
      } catch (error) {
        // A network failure is not a lost session; the upload's own list-parts call retries it.
        if (!isSessionGone(error)) return record;
        resumeStore.clear(resource, data);
        // Expired: the row it fed recorded the session, so the backend can still discard both.
        if ((error as { status?: number }).status === 404 && record.assetId != null) {
          abortSession(record.uploadId, record.assetId).catch(() => undefined);
        }
        return null;
      }
    },

    addFile: (data, meta = {}, resume) => {
      const id = uppy.addFile({
        name: data.name,
        type: data.type,
        data,
        meta: { ...meta, ...(resume?.assetId != null ? { assetId: resume.assetId } : {}) }
      });
      discarded.delete(id);
      if (resume) setResumeState(id, { uploadId: resume.uploadId, key: resume.key });
      return id;
    },

    discard: async (fileIds) => {
      const targets = fileIds
        .map((id) => uppy.getFile(id))
        .filter((file): file is EngineFile => !!file)
        .map((file) => {
          discarded.add(file.id);
          const record = resumeStore.get(resource, fingerprintOf(file));
          resumeStore.clear(resource, fingerprintOf(file));
          return { uploadId: record?.uploadId, assetId: assetIdOf(file) ?? record?.assetId };
        });
      // Stops the requests in flight; the ones that create a row or a session clean up when they return.
      uppy.removeFiles(fileIds.filter((id) => uppy.getFile(id)));
      const removed = await Promise.all(targets.map(({ uploadId, assetId }) => cleanUp(uploadId, assetId)));
      return removed.filter(Boolean).length;
    },

    // Files still in this engine (failed, awaiting Retry) are not interrupted.
    listInterrupted: () =>
      resumeStore
        .list(resource)
        .filter((record) => !uppy.getFiles().some((file) => file.name === record.fileName && file.size === record.fileSize)),

    discardInterrupted: async (record) => {
      resumeStore.clear(resource, resumeStore.fingerprintOf(record));
      await cleanUp(record.uploadId, record.assetId);
    }
  };
};
